import _ from 'lodash';
import Papa from 'papaparse';
import React, { memo, useContext, useRef, useState } from 'react';
import styled from 'styled-components';
import { Currency, DateTime, Hours, Percent, RouteLink } from '~/components';
import ProjectHealthCircle from '~/components/ProjectHealthCircle';
import ProjectHealthScoreBar from '~/components/ProjectHealthScoreBar';
import { MemberContext } from '~/contexts';
import { useNumberFormat } from '~/hooks';
import { dateFormats } from '~/utils';
import { colors } from '~/styles';
import { applyDisplay } from './utils/applyDisplay';
import { resolveLink } from './utils/resolveLink';

const fallbackNumberFormat = new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const fallbackCurrencyFormat = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fallbackPercentFormat = new Intl.NumberFormat(undefined, {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const PREVIEW_ROWS = 3;

const S = {
  Container: styled.div`
    margin: 0.75rem 0;
    overflow-anchor: none;
  `,

  TableScroll: styled.div`
    overflow-x: auto;
    overflow-y: hidden;
    position: relative;
    transition: max-height 0.3s ease;

    > table {
      min-width: 100%;
      border-collapse: collapse;
      font-size: 0.75rem;
      table-layout: auto;

      th,
      td {
        padding: 0.5rem 0.75rem;
      }

      th {
        white-space: nowrap;
        font-weight: 600;
        background: ${colors.grey5};
        border-bottom: 2px solid ${colors.grey20};
      }

      td {
        border-bottom: 1px solid ${colors.grey10};
        min-width: 10rem;
      }

      .col-narrow {
        min-width: 5rem;
      }

      .col-metric {
        min-width: 7rem;
      }

      .col-wide {
        min-width: 20rem;
      }

      tr:hover {
        background: ${colors.grey5};
      }

      tr:last-child td {
        border-bottom: none;
      }

      .align-right {
        text-align: right;
      }

      .align-center {
        text-align: center;
      }

      .subtotal-row td {
        font-weight: 500;
      }

      .total-row td {
        font-weight: 600;
        border-top: 2px solid ${colors.grey20};
      }
    }
  `,

  FadeOverlay: styled.div`
    position: sticky;
    left: 0;
    height: 4.5rem;
    margin-top: -4.5rem;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
    pointer-events: none;
  `,

  ToggleButton: styled.button`
    border: 1px solid ${colors.grey10};
    border-radius: 999rem;
    background: ${colors.white};
    color: ${colors.grey75};
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    

    &:hover {
      background: ${colors.grey5};
      color: ${colors.black};
    }
  `,

  ButtonRow: styled.div`
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.375rem 0;
    opacity: 0.5;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }
  `,
};

function columnClass(field) {
  if (field.wide) return 'col-wide';
  if (field.semantic === 'health-status') return 'col-narrow align-center';
  if (field.semantic === 'health-score') return 'col-metric align-center';
  switch (field.type) {
    case 'date':
    case 'date-time':
    case 'boolean':
      return 'col-narrow';
    case 'number':
    case 'integer':
      return 'col-metric align-right';
    default:
      switch (field.role) {
        case 'time':
          return 'col-narrow';
        case 'metric':
          return 'col-metric align-right';
        default:
          return undefined;
      }
  }
}

function nullDisplay(value, field, totalLabel) {
  if (value == null) {
    if (totalLabel != null) return totalLabel;
    if (field.role === 'metric') return '—';
    return '';
  }
  return undefined;
}

function collectionDisplay(value) {
  if (Array.isArray(value)) {
    const parts = value
      .map((v) => {
        if (v == null) return null;
        if (typeof v === 'object') return v.name ?? v.title ?? v.label ?? null;
        return String(v);
      })
      .filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '—';
  }

  if (typeof value === 'object') {
    return value.name ?? value.title ?? value.label ?? '—';
  }

  return undefined;
}

function commonDisplay(value, field) {
  if (field.type === 'number' || field.type === 'integer') return fallbackNumberFormat.format(value);
  if (field.type === 'boolean') return value ? 'Yes' : 'No';
  if (field.enumValues?.[value]) return field.enumValues[value];
  return undefined;
}

function RichCellValue({ value, field, row, totalLabel }) {
  const numberFormat = useNumberFormat({ minimumFractionDigits: 0, maximumFractionDigits: 2 });

  const nd = nullDisplay(value, field, totalLabel);
  if (nd !== undefined) return nd;

  const cd = collectionDisplay(value);
  if (cd !== undefined) return cd;

  if (field.semantic === 'currency') {
    const currency = field.currencyPath ? _.get(row, field.currencyPath) : undefined;
    if (currency) return <Currency value={value} currency={currency} />;
    return fallbackCurrencyFormat.format(value);
  }

  if (field.semantic === 'hours') return <Hours value={value} />;
  if (field.semantic === 'percent') return <Percent value={value} />;
  if (field.semantic === 'health-status') {
    return (
      <ProjectHealthCircle
        value={{
          id: value,
          label: field.enumValues?.[value] ?? value,
          notes: field.notesPath ? _.get(row, field.notesPath) : undefined,
        }}
      />
    );
  }
  if (field.semantic === 'health-score') return <ProjectHealthScoreBar score={value} />;
  if (field.type === 'date') return <DateTime value={value} format={dateFormats.compactDate} />;
  if (field.type === 'date-time') return <DateTime value={value} format={dateFormats.compactDateTime} />;

  if (field.type === 'number' || field.type === 'integer') return numberFormat.format(value);
  if (field.type === 'boolean') return value ? 'Yes' : 'No';
  if (field.enumValues?.[value]) return field.enumValues[value];

  return String(value);
}

function SimpleCellValue({ value, field, totalLabel }) {
  const nd = nullDisplay(value, field, totalLabel);
  if (nd !== undefined) return nd;

  const cd = collectionDisplay(value);
  if (cd !== undefined) return cd;

  if (field.semantic === 'currency') return fallbackCurrencyFormat.format(value);
  if (field.semantic === 'hours') return fallbackNumberFormat.format(value);
  if (field.semantic === 'percent') return fallbackPercentFormat.format(value);

  const cm = commonDisplay(value, field);
  if (cm !== undefined) return cm;

  return String(value);
}

function AssistantTable({ data, fields, display, meta, pagination, filename }) {
  const memberCtx = useContext(MemberContext);
  const CellValue = memberCtx ? RichCellValue : SimpleCellValue;

  const defaultCollapsed = Array.isArray(data) && data.length > 10;
  const [isExpanded, setIsExpanded] = useState(!defaultCollapsed);
  const tableScrollRef = useRef(null);

  if (!Array.isArray(fields) || fields.length === 0 || !Array.isArray(data) || data.length === 0) return null;

  // Narrow to the LLM's `display` selection (if any). `display` is a Table-only
  // rendering concern — pivot/schedule/chart use the full field catalog.
  const visibleFields = applyDisplay(fields, display);
  const firstDimensionIndex = visibleFields.findIndex((f) => f.role !== 'metric');
  const workspaceKey = meta?.workspaceKey;
  const canCollapse = data.length > PREVIEW_ROWS;
  const visibleRows = isExpanded || !canCollapse ? data : data.slice(0, PREVIEW_ROWS);

  const handleToggle = (expand) => {
    const el = tableScrollRef.current;
    if (!el) {
      setIsExpanded(expand);
      return;
    }

    if (expand) {
      // Pin current height, add rows, animate to new height
      el.style.transition = 'max-height 0.5s ease';
      el.style.maxHeight = `${el.scrollHeight}px`;
      setIsExpanded(true);
      requestAnimationFrame(() => {
        el.style.maxHeight = `${el.scrollHeight}px`;
        // Scroll the table to the top of the chat scroll area
        let scrollParent = el.parentElement;
        while (scrollParent && getComputedStyle(scrollParent).overflowY !== 'auto') {
          scrollParent = scrollParent.parentElement;
        }
        if (scrollParent) {
          const offset = el.parentElement.getBoundingClientRect().top - scrollParent.getBoundingClientRect().top;
          scrollParent.scrollTo({ top: scrollParent.scrollTop + offset, behavior: 'smooth' });
        }
        const onEnd = () => {
          el.style.maxHeight = 'none';
          el.removeEventListener('transitionend', onEnd);
        };
        el.addEventListener('transitionend', onEnd);
      });
    } else {
      // Measure collapsed target from the current DOM before removing rows
      const table = el.querySelector('table');
      const thead = table?.querySelector('thead');
      const rows = table?.querySelectorAll('tbody tr');
      let targetHeight = el.scrollHeight;
      if (thead && rows) {
        targetHeight = thead.offsetHeight;
        for (let i = 0; i < Math.min(PREVIEW_ROWS, rows.length); i++) {
          targetHeight += rows[i].offsetHeight;
        }
      }

      // Pin full height, then animate down to collapsed height
      el.style.transition = 'max-height 0.25s ease';
      el.style.maxHeight = `${el.scrollHeight}px`;
      requestAnimationFrame(() => {
        el.style.maxHeight = `${targetHeight}px`;
        const onEnd = () => {
          // Leave maxHeight at targetHeight — content already fits, no visual change.
          // The expand handler will override it on next toggle.
          setIsExpanded(false);
          el.removeEventListener('transitionend', onEnd);
        };
        el.addEventListener('transitionend', onEnd);
      });
    }
  };

  const handleDownload = () => {
    const rows = data.map((row) =>
      Object.fromEntries(
        fields.map((f) => {
          const header = f.exportLabel ?? f.label;
          const raw = _.get(row, f.path);
          if (raw == null) return [header, ''];
          if (f.enumValues?.[raw]) return [header, f.enumValues[raw]];
          if (Array.isArray(raw))
            return [header, raw.map((v) => (typeof v === 'object' ? (v?.name ?? '') : v)).join(', ')];
          if (typeof raw === 'object') return [header, raw.name ?? raw.title ?? raw.label ?? ''];
          return [header, raw];
        }),
      ),
    );
    const csv = Papa.unparse(rows, { columns: fields.map((f) => f.exportLabel ?? f.label) });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename || 'table'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const remainingCount = pagination?.hasMore
    ? `${data.length - PREVIEW_ROWS}+ more rows`
    : `${data.length - PREVIEW_ROWS} more rows`;

  return (
    <S.Container>
      <S.TableScroll ref={tableScrollRef}>
        <table>
          <thead>
            <tr>
              {visibleFields.map((field) => (
                <th key={field.key} className={columnClass(field)}>
                  {field.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, i) => {
              const isTotal = row.rowType === 'total';
              const isSubtotal = row.rowType === 'subtotal';
              return (
                <tr key={i} className={isTotal ? 'total-row' : isSubtotal ? 'subtotal-row' : undefined}>
                  {visibleFields.map((field, fieldIndex) => {
                    const rawValue = _.get(row, field.path);
                    const link = field.link?.template ? resolveLink(field.link.template, row, workspaceKey) : null;
                    const isDimension = field.role !== 'metric';

                    // Null dimension labels:
                    // - Total row: "Total" (first dimension) / "" (rest)
                    // - Grouped dimension: "None" (labels the null group)
                    // - Non-grouped dimension: blank (handled by CellValue)
                    let totalLabel;
                    if (rawValue == null && isDimension) {
                      if (isTotal) {
                        totalLabel = fieldIndex === firstDimensionIndex ? 'Total' : '';
                      } else if (isSubtotal) {
                        totalLabel = '';
                      } else if (field.grouped) {
                        totalLabel = 'None';
                      }
                    }

                    return (
                      <td key={field.key} className={columnClass(field)}>
                        {link ? (
                          <RouteLink to={link}>
                            <CellValue value={rawValue} field={field} row={row} totalLabel={totalLabel} />
                          </RouteLink>
                        ) : (
                          <CellValue value={rawValue} field={field} row={row} totalLabel={totalLabel} />
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        {!isExpanded && canCollapse && <S.FadeOverlay />}
      </S.TableScroll>
      <S.ButtonRow>
        {canCollapse &&
          (isExpanded ? (
            <S.ToggleButton onClick={() => handleToggle(false)}>
              Show less ({data.length} rows{pagination?.hasMore ? '+' : ''})
            </S.ToggleButton>
          ) : (
            <S.ToggleButton onClick={() => handleToggle(true)}>Show all ({remainingCount})</S.ToggleButton>
          ))}
        <S.ToggleButton onClick={handleDownload}>Save CSV</S.ToggleButton>
      </S.ButtonRow>
    </S.Container>
  );
}

export default memo(AssistantTable);
